import React, { useMemo } from 'react';
import Countdown from 'react-countdown';
import { Box, Typography, type SxProps, type Theme } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { BRAND_LUXE_PRIMARY, BRAND_LUXE_SECONDARY } from '../brandDashboardTokens';

export function getLtdPromoEndTimestamp(endsAt: string | null | undefined): number | null {
    if (!endsAt) return null;
    const ms = new Date(endsAt).getTime();
    if (Number.isNaN(ms)) return null;
    return ms;
}

export function shouldShowLtdPromoCountdown(promoActive: boolean, endsAt: string | null | undefined): boolean {
    const endMs = getLtdPromoEndTimestamp(endsAt);
    return Boolean(promoActive && endMs != null && endMs > Date.now());
}

function formatRemaining(days: number, hours: number, minutes: number, seconds: number): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    if (days > 0) {
        return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export interface CampaignHostLtdPromoCountdownProps {
    endsAt: string | null | undefined;
    /** When false, nothing is rendered even if endsAt is set */
    show?: boolean;
    onComplete?: () => void;
    sx?: SxProps<Theme>;
}

export function CampaignHostLtdPromoCountdown({
    endsAt,
    show = true,
    onComplete,
    sx,
}: CampaignHostLtdPromoCountdownProps) {
    const endMs = useMemo(() => getLtdPromoEndTimestamp(endsAt), [endsAt]);

    if (!show || endMs == null || endMs <= Date.now()) {
        return null;
    }

    return (
        <Countdown
            date={endMs}
            onComplete={onComplete}
            renderer={({ days, hours, minutes, seconds, completed }) => {
                if (completed) {
                    return null;
                }
                const remaining = formatRemaining(days, hours, minutes, seconds);
                return (
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            px: 2,
                            py: 0.75,
                            borderRadius: 99,
                            bgcolor: 'rgba(255, 193, 7, 0.12)',
                            border: `1px solid ${BRAND_LUXE_PRIMARY}44`,
                            ...sx,
                        }}>
                        <AccessTimeIcon sx={{ fontSize: 16, color: BRAND_LUXE_PRIMARY }} />
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 12,
                                letterSpacing: '0.06em',
                                color: BRAND_LUXE_SECONDARY,
                            }}>
                            Offer ends in{' '}
                            <Box component="span" sx={{ color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                                {remaining}
                            </Box>
                        </Typography>
                    </Box>
                );
            }}
        />
    );
}
